import { readFileBuffer } from "../file-system-loader";

interface ImageResponse {
  image: HTMLImageElement
  missing: boolean
  lastAccess: number
  imagePath: string
}

export class ImageCache {
  private queue = [];
  private cache : { [path: string]: ImageResponse } = {};
  private processing = new Set();
  private maxNumProcessing = 2;
  private maxCacheSize = 20;

  async get(imagePath: string, mimeType: string, bypassQueue: boolean): Promise<ImageResponse> {
    if (imagePath in this.cache) {
      // Update last used timestamp and return cached values
      this.cache[imagePath].lastAccess = performance.now();
      return this.cache[imagePath];
    }

    if (this.processing.has(imagePath)) {
      // Image is already loading, just wait for the result
      return this.waitForImage(imagePath);
    }

    if (bypassQueue) {
      // Grab the image, ignoring the processing queue
      await this.loadToCache(imagePath, mimeType);
      return this.cache[imagePath];
    }

    // Add image path to processing queue and wait for the results
    this.queue.push({imagePath, mimeType});
    this.removeOldData();
    this.processQueue();

    return this.waitForImage(imagePath);
  }

  waitForImage(imagePath): Promise<ImageResponse> {
    return new Promise(resolve => {
      const interval = setInterval(async () => {
        if (imagePath in this.cache) {
          clearInterval(interval);
          resolve(this.cache[imagePath]);
        }
      }, 100);
    });
  }

  /** @private */
  async processQueue() {
    if (this.processing.size >= this.maxNumProcessing) return;
    if (this.queue.length === 0) return;

    const {imagePath, mimeType} = this.queue.shift();

    this.processing.add(imagePath);

    if (!this.cache.hasOwnProperty(imagePath)) {
      await this.loadToCache(imagePath, mimeType);
    }

    this.processing.delete(imagePath);
    this.processQueue();
  }

  /** @private */
  async loadToCache(imagePath: string, mimeType: string) {
    let image = null;
    let missing = false;

    try {
      const buffer = await readFileBuffer(imagePath);

      // Load image source from buffer
      let arrayBufferView = new Uint8Array(buffer);
      let blob = new Blob([arrayBufferView], {
        type: mimeType
      });

      // Image successfully loaded
      const blobPath = URL.createObjectURL(blob);
      image = await this.loadImageFromPath(blobPath);
    } catch {
      // Image must be missing
      missing = true;
    }

    const lastAccess = performance.now();

    // Image has been loaded
    this.cache[imagePath] = {
      missing,
      lastAccess,
      image,
      imagePath
    };
  }

  /** @private */
  removeOldData() {
    const keys = Object.keys(this.cache);
    if (keys.length <= this.maxCacheSize) return;

    const data = keys.map(key => this.cache[key]);

    data.sort((a,b) => {
      return a.lastAccess - b.lastAccess;
    });

    data.reverse();

    this.cache = {};

    data.forEach((image, index) => {
      if (index > this.maxCacheSize) return;
      this.cache[image.imagePath] = image;
    });
  }

  /** @private */
  loadImageFromPath(path): Promise<HTMLImageElement> {
    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => {
        resolve(image);
      };
      image.src = path;
    });
  }
}